import {Dispatch, PropsWithChildren, useMemo, useState} from "react";
import {
  createInitialCreateProductReducerStateFromApi,
  createProductReducer,
  initialCreateProductState,
} from "../../reducers/create-product";
import {createContext} from "../../../../../context/create-context";
import {CreateProductActions} from "../../reducers/create-product/types";
import {usePricingCurrencies} from "@/features/currencies";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  usePersistedReducer,
} from "../../../../../utils/offering-create-draft";

type CreateProductContextState = {
  state: typeof initialCreateProductState;
  dispatch: Dispatch<CreateProductActions>;
  wasDraftRestored: boolean;

  variantCurrencies: string[];
  setVariantCurrencies: React.Dispatch<React.SetStateAction<string[]>>;
};

const [useContext, ContextProvider] = createContext<CreateProductContextState>();

export const useCreateProductContext = useContext;

export const CreateProductProvider = ({children}: PropsWithChildren) => {
  const {state: profileState} = useGetProfileContext();
  const pricingCurrencies = usePricingCurrencies();

  // Snapshot values once for seeding.
  // If your currency context is async, guard with a fallback until ready.
  const bankConnected = !!pricingCurrencies.connectedBankStatus.isBankConnected;
  const defaultCurrency = pricingCurrencies.formCurrencies[0];

  const getInitialState = () =>
    createInitialCreateProductReducerStateFromApi({
      bankConnected,
      defaultCurrency: defaultCurrency ?? "NGN",
    });

  const draftScope = useMemo(
    () => ({
      draftKey: OFFERING_CREATE_DRAFT_KEYS.product,
      email: profileState.profileData.email,
    }),
    [profileState.profileData.email]
  );

  const [state, dispatch, {wasDraftRestored}] = usePersistedReducer(
    createProductReducer,
    draftScope,
    getInitialState
  );

  const [variantCurrencies, setVariantCurrencies] = useState<string[]>([]);

  // function handleOptionPriceType () {
  //   if(currencyContextState.connectedBankStatus.isBankConnected) {
  //     if(state.)
  //     return "paid"
  //   }
  // }

  // const newState: CreateProductState = {
  //   currentCreateProductStep: state.currentCreateProductStep,
  //   formData: {
  //     ...state.formData,
  //     priceType: state.formData.priceType
  //       ? state.formData.priceType
  //       : currencyContextState.connectedBankStatus.isBankConnected
  //       ? "paid"
  //       : "free",
  //     currencies:
  //       state.formData.currencies.length > 0
  //         ? state.formData.currencies
  //         : [
  //             {
  //               callPrice: 100,
  //               callPriceCurrency: currencyContextState.formCurrencies[0],
  //             },
  //           ],
  //   },
  //   apiResponseData: state.apiResponseData,
  //   variantFormData: {
  //     showVariants: state.variantFormData.showVariants,
  //     variants: state.variantFormData.variants.map((v) => ({
  //       name: v.name,
  //       options: v.options.map((o) => ({
  //         name: o.name,
  //         priceType: currencyContextState.connectedBankStatus.isBankConnected
  //           ? "paid"
  //           : "free",
  //       })),
  //     })),
  //   },
  // };

  const value = useMemo(
    () => ({state, dispatch, wasDraftRestored, variantCurrencies, setVariantCurrencies}),
    [state, wasDraftRestored, variantCurrencies]
  );

  // If currency context isn't ready yet, you can early-return null or a loader
  // to avoid seeding with undefined. Adjust to your app's UX.
  if (!defaultCurrency) {
    return null; // or a loader component
  }

  return <ContextProvider value={value}>{children}</ContextProvider>;
};
